import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Check, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { useAgentSettings } from '@/lib/agentSettings'
import AITermsDocument from '@/components/listings/ai/AITermsDocument'
import { AI_TERMS_VERSION } from '@/components/listings/ai/aiTermsContent'

/**
 * The AI terms on a page of their own, so an agent can read, print or link
 * to them outside the moment of accepting. Accepting works here too, for
 * anyone who wants to agree before they first open AI mode.
 */
export default function AITermsPage() {
    const { settings, loaded, save } = useAgentSettings()
    const [ticked, setTicked] = useState(false)
    const [saving, setSaving] = useState(false)
    const accepted = Boolean(settings.aiTermsAcceptedAt)
    const outdated = accepted && settings.aiTermsVersion !== AI_TERMS_VERSION

    const accept = async () => {
        setSaving(true)
        try {
            await save({ acceptAiTerms: true })
            toast.success('Accepted. Thank you.')
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'That could not be saved. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='mx-auto w-full max-w-3xl pb-16'>
            <div className='flex flex-wrap items-center justify-between gap-3 print:hidden'>
                <Link to='/settings' className='flex items-center gap-1.5 text-sm text-muted-foreground hover:text-black'>
                    <ArrowLeft className='h-4 w-4' /> Settings
                </Link>
                <Button size='sm' variant='outline' onClick={() => window.print()}>
                    <Printer className='h-4 w-4' /> Print
                </Button>
            </div>

            <div className='mt-5 rounded-2xl border p-6'>
                <AITermsDocument />
            </div>

            <div className='mt-4 rounded-2xl border p-4 print:hidden'>
                {!loaded ? (
                    <Spinner variant='circle' className='h-4 w-4' />
                ) : accepted && !outdated ? (
                    <p className='inline-flex items-center gap-1.5 text-sm text-green-700'>
                        <Check className='h-4 w-4' />
                        Accepted on{' '}
                        {new Date(settings.aiTermsAcceptedAt as string).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {settings.aiTermsVersion ? ` · version ${settings.aiTermsVersion}` : ''}
                    </p>
                ) : (
                    <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-3'>
                        <label className='flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-sm'>
                            <Checkbox checked={ticked} onCheckedChange={(next) => setTicked(Boolean(next))} className='mt-0.5' />
                            <span>
                                {outdated
                                    ? 'These terms have been revised since you accepted them. I have read the new version and accept it.'
                                    : 'I have read these terms and accept them on behalf of my listings.'}
                            </span>
                        </label>
                        <Button className='ml-auto shrink-0' disabled={!ticked || saving} onClick={accept}>
                            {saving ? <Spinner variant='circle' className='h-4 w-4' /> : 'Accept'}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}
